"use client";

import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bot, User } from "lucide-react";
import { AeraImage } from "./DataTableImage";
import { useHistoryActorProfile } from "../utils/historyActorProfile";

const AVATAR_SIZE_PX = 20;

function HistoryActorInitials({ label }: { label: string }) {
  const trimmed = label.trim();
  const initial = trimmed ? trimmed.charAt(0).toUpperCase() : "?";
  return (
    <span className="text-[9px] font-bold leading-none text-aera-gray">
      {initial}
    </span>
  );
}

export function HistoryActorAvatar({
  actorId,
  actorLabel,
}: {
  actorId?: string | null;
  actorLabel?: string | null;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const { displayName, avatarUrl, kind } = useHistoryActorProfile(
    actorId,
    actorLabel,
  );

  if (!displayName && !actorId?.trim()) return null;

  const tooltip = displayName?.trim() || null;
  const ariaLabel = tooltip ? `ผู้ดำเนินการ: ${tooltip}` : "ผู้ดำเนินการ";

  const updateTooltipPosition = useCallback(() => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showTooltip = () => {
    updateTooltipPosition();
    setTooltipOpen(true);
  };

  const hideTooltip = () => {
    setTooltipOpen(false);
  };

  return (
    <>
      <span
        ref={anchorRef}
        className="inline-flex shrink-0"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onMouseMove={updateTooltipPosition}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <span
          className="flex items-center justify-center overflow-hidden rounded-full bg-aera-lightgray ring-1 ring-aera-lightgray/80"
          style={{ width: AVATAR_SIZE_PX, height: AVATAR_SIZE_PX }}
          aria-label={ariaLabel}
          tabIndex={0}
        >
          {avatarUrl ? (
            <AeraImage
              src={avatarUrl}
              alt=""
              width={AVATAR_SIZE_PX}
              height={AVATAR_SIZE_PX}
              className="h-full w-full object-cover"
            />
          ) : kind === "system" ? (
            <Bot className="h-3 w-3 text-aera-gray" aria-hidden />
          ) : kind === "line" ? (
            <User className="h-3 w-3 text-aera-gray" aria-hidden />
          ) : displayName ? (
            <HistoryActorInitials label={displayName} />
          ) : (
            <User className="h-3 w-3 text-aera-gray" aria-hidden />
          )}
        </span>
      </span>
      {tooltipOpen && tooltip && typeof document !== "undefined"
        ? createPortal(
            <span
              role="tooltip"
              className="pointer-events-none fixed z-[10200] -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-md bg-aera-charcoal px-2 py-1 text-[10px] font-semibold text-white shadow-md"
              style={{ top: tooltipPos.top, left: tooltipPos.left }}
            >
              {tooltip}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
